/**
 * DevConsole section dedicated to the on-chain mcap-flywheel loop — the
 * surfpool-testable surface of `last_touch`'s milestone bonus system.
 *
 * Companion to TokenStubsSection (which fakes events client-side). This
 * section drives the *real* on-chain path end-to-end:
 *   1. `koth_init_season` with a configurable MilestoneConfig
 *   2. `koth_init_payout_vault` so a vault PDA exists
 *   3. SystemProgram.transfer to fund that vault (so the fraction is
 *      meaningful — without lamports the latch pays out zero)
 *   4. `koth_latch_milestone` with the leaderboard-derived top-K
 *   5. Live Season snapshot proves `useFlywheelOnChainBridge` is polling
 *   6. Live MilestoneCrossing log proves the program-log subscription +
 *      `parseLatchLog` are decoding `koth_latch_milestone:` lines into
 *      events that fan out to MilestoneToast.
 *
 * All ix wiring intentionally mirrors ChainTab's existing helpers; this
 * section is the focused testing surface for the flywheel without the
 * full ix-grid noise.
 */
import { BN } from "@coral-xyz/anchor";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  Transaction,
} from "@solana/web3.js";
import { Buffer } from "buffer";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  buildWalletProgram,
  deriveRoundPda,
  deriveSeasonPda,
} from "../../games/last-touch/anchor";
import { KOTH_ROUND_LS_KEY } from "../../games/last-touch/useKothLobbyController";
import { milestoneTargets } from "../../token/milestoneTargets";
import { useMilestoneLog } from "../../token/milestoneEvents";
import { btnStyle, colors, fmtSol, shortKey, styles } from "./_shared";

const SEASON_LS_KEY = "solblox.season_id_hex";

interface MethodsBuilder {
  accounts(accounts: Record<string, unknown>): MethodsBuilder;
  remainingAccounts(a: { pubkey: PublicKey; isWritable: boolean; isSigner: boolean }[]): MethodsBuilder;
  rpc(): Promise<string>;
}

interface RoundParamsArg {
  baseFeeLamports: BN;
  stepFeeLamports: BN;
  baseDurationSecs: number;
  extensionSecsPerTouch: number;
  maxTotalExtensionSecs: number;
  cooldownSecs: number;
  protocolSkimBps: number;
  topNPayout: number;
  keeperBountyLamports: BN;
  cooldownSecsPerWallet: number;
  maxTouchesPerWallet: number;
  endgameThresholdSecs: number;
  extensionSecsEndgame: number;
}

interface MilestoneConfigArg {
  initialMilestoneMcapLamports: BN;
  multiplierBps: number;
  topK: number;
  minResidencySlots: BN;
  vaultFractionBps: number;
  mcapKeeper: PublicKey;
}

interface KothMethods {
  kothInitSeason(
    seasonId: number[],
    index: number,
    endsAt: BN,
    params: RoundParamsArg,
    jackpotLamports: BN,
    milestone: MilestoneConfigArg,
  ): MethodsBuilder;
  kothInitPayoutVault(): MethodsBuilder;
  kothLatchMilestone(postedMcapLamports: BN): MethodsBuilder;
}

type RowStatus = "idle" | "running" | "ok" | "fail";

interface RowResult {
  status: RowStatus;
  msg?: string;
  sig?: string;
}

const blankResult: RowResult = { status: "idle" };

function readSeasonHex(): string {
  if (typeof localStorage === "undefined") return "";
  return localStorage.getItem(SEASON_LS_KEY) ?? "";
}

function readRoundIdLs(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(KOTH_ROUND_LS_KEY);
}

function seasonHexToBytes(hex: string): Uint8Array | null {
  if (!hex || hex.length !== 64) return null;
  try {
    const out = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
      out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
  } catch {
    return null;
  }
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function defaultRoundParams(): RoundParamsArg {
  return {
    baseFeeLamports: new BN(1_000_000),
    stepFeeLamports: new BN(100_000),
    baseDurationSecs: 600,
    extensionSecsPerTouch: 3,
    maxTotalExtensionSecs: 30,
    cooldownSecs: 0,
    protocolSkimBps: 200,
    topNPayout: 3,
    keeperBountyLamports: new BN(500_000),
    cooldownSecsPerWallet: 0,
    maxTouchesPerWallet: 1000,
    endgameThresholdSecs: 30,
    extensionSecsEndgame: 10,
  };
}

export function McapFlywheelSection(): JSX.Element {
  const { connection } = useConnection();
  const wallet = useWallet();
  const walletReady = wallet.connected && !!wallet.publicKey;

  // Tracked season id (hex). Re-read on every render via state so this
  // section reflects writes made by ChainTab's init_season in the same
  // browser session.
  const [seasonHex, setSeasonHex] = useState<string>(readSeasonHex);
  const seasonBytes = useMemo(() => seasonHexToBytes(seasonHex), [seasonHex]);
  const seasonPda = useMemo<PublicKey | null>(() => {
    if (!seasonBytes) return null;
    try {
      return deriveSeasonPda(seasonBytes);
    } catch {
      return null;
    }
  }, [seasonBytes]);

  const vaultPda = useMemo<PublicKey | null>(() => {
    if (!seasonPda) return null;
    return PublicKey.findProgramAddressSync(
      [Buffer.from("season_payout_vault"), seasonPda.toBuffer()],
      // Program ID is read from the wallet program on demand; for derivation
      // we use the same constant ChainTab uses to avoid an unnecessary build.
      new PublicKey("DcRZHfX9T6fjbD768mG2e21aon224Fu8e8gsihR3R6uR"),
    )[0];
  }, [seasonPda]);

  // Milestone config inputs — used for the init-season call only.
  const [initialMcapInputSol, setInitialMcapInputSol] = useState("10");
  const [multiplierBpsInput, setMultiplierBpsInput] = useState("20000");
  const [topKInput, setTopKInput] = useState("3");
  const [vaultFractionBpsInput, setVaultFractionBpsInput] = useState("1000");

  // Latch-call inputs.
  const [postedMcapInputSol, setPostedMcapInputSol] = useState("15");
  const [fundVaultInputSol, setFundVaultInputSol] = useState("1");

  // Live Season snapshot — polled here for visibility (in addition to the
  // bridge in App.tsx). Keeps the section legible even when the user has
  // never opened TokenMcapPanel in this session.
  const [seasonAcct, setSeasonAcct] = useState<Record<string, unknown> | null>(null);
  const [vaultLamports, setVaultLamports] = useState<number | null>(null);
  const fetchSeason = useCallback(async () => {
    if (!seasonPda || !walletReady) {
      setSeasonAcct(null);
      return;
    }
    try {
      const program = buildWalletProgram(connection, wallet);
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["season"];
      if (!ns) {
        setSeasonAcct(null);
        return;
      }
      const acct = (await ns.fetch(seasonPda)) as Record<string, unknown>;
      setSeasonAcct(acct);
    } catch {
      setSeasonAcct(null);
    }
  }, [connection, wallet, walletReady, seasonPda]);

  const fetchVaultBalance = useCallback(async () => {
    if (!vaultPda) {
      setVaultLamports(null);
      return;
    }
    try {
      const lamports = await connection.getBalance(vaultPda, "confirmed");
      setVaultLamports(lamports);
    } catch {
      setVaultLamports(null);
    }
  }, [connection, vaultPda]);

  useEffect(() => {
    void fetchSeason();
    void fetchVaultBalance();
    const t = setInterval(() => {
      void fetchSeason();
      void fetchVaultBalance();
    }, 8_000);
    return () => clearInterval(t);
  }, [fetchSeason, fetchVaultBalance]);

  // Row-result tracking — local to this section so each button has its own
  // pill status without colliding with ChainTab's state.
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const setRow = useCallback((id: string, r: RowResult) => {
    setResults((prev) => ({ ...prev, [id]: r }));
  }, []);
  const runWith = useCallback(
    async (id: string, fn: () => Promise<{ sig?: string; msg?: string }>) => {
      setRow(id, { status: "running" });
      try {
        const { sig, msg } = await fn();
        setRow(id, { status: "ok", sig, msg });
      } catch (e) {
        setRow(id, {
          status: "fail",
          msg: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [setRow],
  );

  const initSeason = useCallback(() => {
    void runWith("initSeason", async () => {
      if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
      const initialSol = Number(initialMcapInputSol);
      if (!Number.isFinite(initialSol) || initialSol <= 0)
        throw new Error("initial threshold (SOL) must be > 0");
      const multBps = parseInt(multiplierBpsInput, 10);
      if (!Number.isFinite(multBps) || multBps < 10_000)
        throw new Error("multiplier_bps must be ≥ 10000 (1.0×)");
      const topK = parseInt(topKInput, 10);
      if (!Number.isFinite(topK) || topK <= 0 || topK > 16)
        throw new Error("top_k must be in 1..=16");
      const fracBps = parseInt(vaultFractionBpsInput, 10);
      if (!Number.isFinite(fracBps) || fracBps <= 0 || fracBps > 10_000)
        throw new Error("vault_fraction_bps must be in 1..=10000");

      const seasonIdRaw = new Uint8Array(32);
      crypto.getRandomValues(seasonIdRaw);
      const hex = bytesToHex(seasonIdRaw);
      const program = buildWalletProgram(connection, wallet);
      const methods = program.methods as unknown as KothMethods;
      const newSeasonPda = deriveSeasonPda(seasonIdRaw, program.programId);
      const endsAt = new BN(Math.floor(Date.now() / 1000) + 24 * 60 * 60);
      const milestone: MilestoneConfigArg = {
        initialMilestoneMcapLamports: new BN(Math.floor(initialSol)).mul(
          new BN(LAMPORTS_PER_SOL),
        ),
        multiplierBps: multBps,
        topK,
        // Must be >= 1 — MilestoneConfig::validate rejects 0 (audit milestone-3).
        minResidencySlots: new BN(1),
        vaultFractionBps: fracBps,
        mcapKeeper: wallet.publicKey,
      };
      const sig = await methods
        .kothInitSeason(
          Array.from(seasonIdRaw),
          1,
          endsAt,
          defaultRoundParams(),
          new BN(0),
          milestone,
        )
        .accounts({
          season: newSeasonPda,
          admin: wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(SEASON_LS_KEY, hex);
      }
      setSeasonHex(hex);
      void fetchSeason();
      return {
        sig,
        msg: `season ${shortKey(newSeasonPda.toBase58())} · next=${initialSol} SOL · ${multBps / 100}% mult · top-${topK} · vault ${fracBps / 100}%`,
      };
    });
  }, [
    connection,
    wallet,
    walletReady,
    initialMcapInputSol,
    multiplierBpsInput,
    topKInput,
    vaultFractionBpsInput,
    runWith,
    fetchSeason,
  ]);

  const initPayoutVault = useCallback(() => {
    void runWith("initPayoutVault", async () => {
      if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
      if (!seasonPda || !vaultPda)
        throw new Error("no season tracked — init a season first");
      const existing = await connection.getAccountInfo(vaultPda);
      if (existing !== null) {
        await fetchVaultBalance();
        return { msg: `vault already exists at ${shortKey(vaultPda.toBase58())}` };
      }
      const program = buildWalletProgram(connection, wallet);
      const methods = program.methods as unknown as KothMethods;
      const sig = await methods
        .kothInitPayoutVault()
        .accounts({
          season: seasonPda,
          vault: vaultPda,
          admin: wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();
      await fetchVaultBalance();
      return { sig, msg: `vault ready at ${shortKey(vaultPda.toBase58())}` };
    });
  }, [connection, wallet, walletReady, seasonPda, vaultPda, fetchVaultBalance, runWith]);

  const fundVault = useCallback(() => {
    void runWith("fundVault", async () => {
      if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
      if (!vaultPda) throw new Error("no vault PDA — init a season first");
      const sol = Number(fundVaultInputSol);
      if (!Number.isFinite(sol) || sol <= 0)
        throw new Error("fund amount must be > 0 SOL");
      const lamports = Math.floor(sol * LAMPORTS_PER_SOL);
      if (!wallet.sendTransaction)
        throw new Error("connected wallet cannot sendTransaction");
      const tx = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: wallet.publicKey,
          toPubkey: vaultPda,
          lamports,
        }),
      );
      const sig = await wallet.sendTransaction(tx, connection);
      const latest = await connection.getLatestBlockhash("confirmed");
      await connection.confirmTransaction(
        { signature: sig, ...latest },
        "confirmed",
      );
      await fetchVaultBalance();
      return { sig, msg: `transferred ${sol} SOL → vault` };
    });
  }, [connection, wallet, walletReady, vaultPda, fundVaultInputSol, fetchVaultBalance, runWith]);

  const latchMilestone = useCallback(() => {
    void runWith("latchMilestone", async () => {
      if (!walletReady || !wallet.publicKey) throw new Error("connect wallet");
      if (!seasonPda) throw new Error("no season tracked — init a season first");
      const roundIdStr = readRoundIdLs();
      if (!roundIdStr)
        throw new Error(
          "no active round in localStorage (koth.activeRoundId) — start a round from ChainTab or the lobby first",
        );
      const roundId = new BN(roundIdStr);
      const program = buildWalletProgram(connection, wallet);
      const methods = program.methods as unknown as KothMethods;
      const sol = Number(postedMcapInputSol);
      if (!Number.isFinite(sol) || sol <= 0)
        throw new Error("posted mcap (SOL) must be > 0");
      const postedMcapLamports = new BN(Math.floor(sol)).mul(new BN(LAMPORTS_PER_SOL));

      const ns = program.account as unknown as Record<
        string,
        {
          fetch: (pk: PublicKey) => Promise<unknown>;
          all: () => Promise<{ publicKey: PublicKey; account: Record<string, unknown> }[]>;
        }
      >;
      const seasonNs = ns["season"];
      const lbNs = ns["leaderboardEntry"];
      if (!seasonNs || !lbNs)
        throw new Error("program.account.{season,leaderboardEntry} unavailable");
      const acct = (await seasonNs.fetch(seasonPda)) as Record<string, unknown>;
      const topK = Number(acct.milestoneTopK ?? acct.milestone_top_k ?? 0);
      if (!topK) throw new Error("season has milestoneTopK = 0");

      const roundPda = deriveRoundPda(roundId, program.programId);
      const entries = await lbNs.all();
      const matched = entries.filter((e) => {
        const r = e.account.round as PublicKey;
        return r && r.toBase58 && r.toBase58() === roundPda.toBase58();
      });
      matched.sort((a, b) => {
        const sa = BigInt((a.account.holdScore ?? a.account.hold_score ?? 0).toString());
        const sb = BigInt((b.account.holdScore ?? b.account.hold_score ?? 0).toString());
        if (sb > sa) return 1;
        if (sb < sa) return -1;
        return 0;
      });
      if (matched.length < topK)
        throw new Error(
          `need ${topK} LeaderboardEntry PDAs for the active round, only ${matched.length} found — run more touches first`,
        );
      const winners = matched.slice(0, topK);
      const vaultPdaLocal = PublicKey.findProgramAddressSync(
        [Buffer.from("season_payout_vault"), seasonPda.toBuffer()],
        program.programId,
      )[0];
      const remaining = winners.map((w) => ({
        pubkey: w.publicKey,
        isWritable: true,
        isSigner: false,
      }));
      const sig = await methods
        .kothLatchMilestone(postedMcapLamports)
        .accounts({
          season: seasonPda,
          round: roundPda,
          payoutVault: vaultPdaLocal,
          keeper: wallet.publicKey,
        })
        .remainingAccounts(remaining)
        .rpc();
      void fetchSeason();
      void fetchVaultBalance();
      return {
        sig,
        msg: `posted ${sol} SOL, credited ${topK} winners — check the log below for the parsed crossing`,
      };
    });
  }, [
    connection,
    wallet,
    walletReady,
    seasonPda,
    postedMcapInputSol,
    fetchSeason,
    fetchVaultBalance,
    runWith,
  ]);

  const refreshSeasonHex = useCallback(() => setSeasonHex(readSeasonHex()), []);
  const clearTracked = useCallback(() => {
    if (typeof localStorage !== "undefined") localStorage.removeItem(SEASON_LS_KEY);
    setSeasonHex("");
    setSeasonAcct(null);
    setVaultLamports(null);
  }, []);

  const entries = useMilestoneLog((s) => s.entries);
  const clearLog = useMilestoneLog((s) => s.clear);

  const bridgeCache = milestoneTargets.sample(0);

  // Pulled directly off the live Season account so the user can see the
  // bridge polling actually shape the numbers without leaving this section.
  const seasonPeak = (seasonAcct?.peakMcapLamports ?? seasonAcct?.peak_mcap_lamports) as
    | BN
    | bigint
    | number
    | undefined;
  const seasonNext = (seasonAcct?.nextMilestoneMcapLamports ??
    seasonAcct?.next_milestone_mcap_lamports) as BN | bigint | number | undefined;
  const seasonMult = (seasonAcct?.milestoneMultiplierBps ??
    seasonAcct?.milestone_multiplier_bps) as number | undefined;
  const seasonTopK = (seasonAcct?.milestoneTopK ?? seasonAcct?.milestone_top_k) as
    | number
    | undefined;
  const seasonFracBps = (seasonAcct?.milestoneVaultFractionBps ??
    seasonAcct?.milestone_vault_fraction_bps) as number | undefined;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.sectionHeader}>
        <div style={styles.sectionTitle}>Mcap flywheel (on-chain)</div>
        <div style={styles.sectionSubtitle}>surfpool-testable</div>
      </div>

      <div
        style={{
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          overflowY: "auto",
        }}
      >
        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>Tracked season</div>
          <div style={{ ...styles.mono, fontSize: 11 }}>
            {seasonPda ? seasonPda.toBase58() : "(none — run init_season below)"}
          </div>
          <div style={{ ...styles.label, color: colors.textFaint }}>
            id (hex){" "}
            {seasonHex ? `${seasonHex.slice(0, 16)}…${seasonHex.slice(-6)}` : "—"}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={refreshSeasonHex} style={btnStyle({ small: true })}>
              re-read LS
            </button>
            <button onClick={clearTracked} style={btnStyle({ small: true, tone: "ghost" })}>
              clear
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>
            1) koth_init_season — MilestoneConfig
          </div>
          <div style={styles.row}>
            <span style={{ ...styles.label, width: 110, color: colors.textMute }}>
              initial (SOL)
            </span>
            <input
              type="number"
              value={initialMcapInputSol}
              onChange={(e) => setInitialMcapInputSol(e.target.value)}
              style={styles.inputStyle}
            />
          </div>
          <div style={styles.row}>
            <span style={{ ...styles.label, width: 110, color: colors.textMute }}>
              multiplier bps
            </span>
            <input
              type="number"
              value={multiplierBpsInput}
              onChange={(e) => setMultiplierBpsInput(e.target.value)}
              style={styles.inputStyle}
            />
            <span style={{ ...styles.label, color: colors.textFaint }}>
              {(Number(multiplierBpsInput) || 0) / 10_000}×
            </span>
          </div>
          <div style={styles.row}>
            <span style={{ ...styles.label, width: 110, color: colors.textMute }}>
              top_k
            </span>
            <input
              type="number"
              value={topKInput}
              onChange={(e) => setTopKInput(e.target.value)}
              style={styles.inputStyle}
            />
          </div>
          <div style={styles.row}>
            <span style={{ ...styles.label, width: 110, color: colors.textMute }}>
              vault frac bps
            </span>
            <input
              type="number"
              value={vaultFractionBpsInput}
              onChange={(e) => setVaultFractionBpsInput(e.target.value)}
              style={styles.inputStyle}
            />
            <span style={{ ...styles.label, color: colors.textFaint }}>
              {(Number(vaultFractionBpsInput) || 0) / 100}%
            </span>
          </div>
          <Row
            label="Init season"
            onRun={initSeason}
            disabled={!walletReady}
            result={results.initSeason ?? blankResult}
          />
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>
            2) Payout vault
          </div>
          <div style={{ ...styles.mono, fontSize: 11 }}>
            {vaultPda ? `${vaultPda.toBase58()}` : "(no season → no vault)"}
          </div>
          <div style={{ ...styles.label, color: colors.textFaint }}>
            balance{" "}
            {vaultLamports === null ? "—" : `${fmtSol(vaultLamports)} SOL`}
          </div>
          <Row
            label="Init payout vault"
            onRun={initPayoutVault}
            disabled={!walletReady || !seasonPda}
            result={results.initPayoutVault ?? blankResult}
          />
          <div style={styles.row}>
            <span style={{ ...styles.label, width: 110, color: colors.textMute }}>
              fund (SOL)
            </span>
            <input
              type="number"
              value={fundVaultInputSol}
              onChange={(e) => setFundVaultInputSol(e.target.value)}
              style={styles.inputStyle}
            />
          </div>
          <Row
            label="Fund vault (SystemProgram.transfer)"
            onRun={fundVault}
            disabled={!walletReady || !vaultPda}
            result={results.fundVault ?? blankResult}
          />
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>
            3) koth_latch_milestone
          </div>
          <div style={{ fontSize: 11, color: colors.textMute }}>
            Posts a mcap above <code>next_milestone</code>. Pulls top-K
            LeaderboardEntry PDAs for the round id in localStorage and
            attaches them as remainingAccounts. The program log fans out
            via <code>parseLatchLog</code> into the entries below.
          </div>
          <div style={styles.row}>
            <span style={{ ...styles.label, width: 110, color: colors.textMute }}>
              posted (SOL)
            </span>
            <input
              type="number"
              value={postedMcapInputSol}
              onChange={(e) => setPostedMcapInputSol(e.target.value)}
              style={styles.inputStyle}
            />
          </div>
          <Row
            label="Latch milestone"
            onRun={latchMilestone}
            disabled={!walletReady || !seasonPda}
            result={results.latchMilestone ?? blankResult}
          />
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>
            Live Season snapshot (polled here every 8s)
          </div>
          {!seasonAcct ? (
            <div style={{ ...styles.mono, color: colors.textMute }}>
              (no account — init a season or wait for the next poll)
            </div>
          ) : (
            <>
              <div style={styles.mono}>peak {fmtSol(seasonPeak ?? 0)} SOL</div>
              <div style={styles.mono}>next {fmtSol(seasonNext ?? 0)} SOL</div>
              <div style={styles.mono}>
                multiplier{" "}
                {seasonMult === undefined ? "—" : `${seasonMult} bps (${seasonMult / 10_000}×)`}
              </div>
              <div style={styles.mono}>
                top_k {seasonTopK ?? "—"} · vault_frac{" "}
                {seasonFracBps === undefined ? "—" : `${seasonFracBps} bps (${seasonFracBps / 100}%)`}
              </div>
            </>
          )}
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>
            Bridge cache (milestoneTargets — drives TokenMcapPanel)
          </div>
          <div style={styles.mono}>
            source <strong>{bridgeCache.source}</strong>
          </div>
          <div style={styles.mono}>peak {fmtSol(bridgeCache.peakLamports)} SOL</div>
          <div style={styles.mono}>
            next {fmtSol(bridgeCache.nextThresholdLamports)} SOL · {bridgeCache.multiplierBps / 10_000}×
          </div>
          <div style={{ ...styles.label, color: colors.textFaint }}>
            App-root <code>useFlywheelOnChainBridge</code> writes this every 12s.
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>
            MilestoneCrossing log ({entries.length})
          </div>
          <div style={{ fontSize: 11, color: colors.textFaint }}>
            On-chain latches arrive here via{" "}
            <code>connection.onLogs → parseLatchLog → emitOnchain</code>.
            Same store feeds <code>MilestoneToast</code>.
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={clearLog} style={btnStyle({ tone: "ghost", small: true })}>
              clear log
            </button>
          </div>
          {entries.length === 0 ? (
            <div style={{ ...styles.mono, color: colors.textMute }}>(empty)</div>
          ) : (
            entries.map((e) => (
              <div key={e.id} style={{ ...styles.mono, fontSize: 10 }}>
                #{e.id} [{e.source}] posted {fmtSol(e.postedMcapLamports)} SOL · threshold{" "}
                {fmtSol(e.thresholdLamports)} SOL · payout {fmtSol(e.payoutLamports)} SOL ·{" "}
                {e.winners.length} winners
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Row(props: {
  label: string;
  onRun: () => void;
  disabled?: boolean;
  result: RowResult;
}): JSX.Element {
  const { label, onRun, disabled, result } = props;
  const tone = pillTone(result.status);
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        padding: "6px 0",
      }}
    >
      <button
        onClick={onRun}
        disabled={disabled || result.status === "running"}
        style={btnStyle({
          tone: "primary",
          small: true,
          disabled: disabled || result.status === "running",
        })}
      >
        Run
      </button>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={{ color: colors.text, fontSize: 12, fontWeight: 600 }}>
          {label}
        </div>
        {result.msg && (
          <div
            style={{
              ...styles.mono,
              fontSize: 11,
              color: result.status === "fail" ? colors.fail : colors.mono,
              marginTop: 2,
            }}
          >
            {result.msg}
          </div>
        )}
        {result.sig && (
          <div
            style={{
              ...styles.mono,
              fontSize: 10,
              color: colors.accent,
              marginTop: 2,
            }}
          >
            tx {result.sig.slice(0, 8)}…{result.sig.slice(-6)}
          </div>
        )}
      </div>
      <span
        style={{
          padding: "2px 8px",
          borderRadius: 10,
          background: tone.bg,
          color: tone.fg,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          minWidth: 56,
          textAlign: "center",
        }}
      >
        {tone.label}
      </span>
    </div>
  );
}

function pillTone(status: RowStatus): { bg: string; fg: string; label: string } {
  switch (status) {
    case "running":
      return { bg: "#23344f", fg: colors.running, label: "RUNNING" };
    case "ok":
      return { bg: "#1c3a26", fg: colors.ok, label: "OK" };
    case "fail":
      return { bg: "#3a1c26", fg: colors.fail, label: "FAIL" };
    default:
      return { bg: "#22222e", fg: colors.textMute, label: "IDLE" };
  }
}
